import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Package, FlaskConical, Factory } from "lucide-react";
import { PRODUCTS, getProduct } from "../data/products";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 500], [0, 120]);

  if (!product) return <Navigate to="/products" replace />;

  const idx = PRODUCTS.findIndex((p) => p.slug === slug);
  const prev = PRODUCTS[(idx - 1 + PRODUCTS.length) % PRODUCTS.length];
  const next = PRODUCTS[(idx + 1) % PRODUCTS.length];

  return (
    <>
      <section data-testid="product-hero" className="relative isolate flex min-h-[70vh] items-end overflow-hidden noise-surface">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img src={product.image} alt={product.name} className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/75 to-[#0A101D]/40" />
        </motion.div>
        <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Link
            to="/products"
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-amber-500"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All products
          </Link>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.35em] text-amber-500" data-testid="product-code">
            {product.code} — Grade 0{idx + 1}
          </p>
          <h1 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.98] text-white sm:text-7xl">
            <MaskedLine delay={0.15}>{product.shortName}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300"
          >
            {product.tagline}
          </motion.p>
        </div>
      </section>

      <section data-testid="product-overview" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[1.4fr_1fr] lg:px-8">
          <div>
            <Reveal><Eyebrow>Overview</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-700">{product.description}</p>
            </Reveal>
            {product.gradeGroups && (
              <Reveal delay={0.14} className="mt-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Available grades</p>
                <div className="mt-4 space-y-3" data-testid="product-grades">
                  {product.gradeGroups.map((group) => (
                    <div key={group.label} className="flex flex-wrap items-center gap-2">
                      <span className="w-36 shrink-0 text-xs text-slate-500">{group.label}</span>
                      {group.items.map((g) => (
                        <span key={g} className="border border-amber-500/40 bg-amber-500/5 px-3 py-1.5 font-mono text-xs font-semibold text-amber-600">{g}</span>
                      ))}
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            <Reveal delay={0.18} className="mt-12">
              <div className="flex items-center gap-3">
                <Factory className="h-5 w-5 text-amber-500" />
                <h2 className="font-display text-2xl font-semibold uppercase text-[#0E1526]">Applications</h2>
              </div>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="product-applications">
                {product.applications.map((a) => (
                  <span key={a} className="border border-black/10 bg-[#F5F5F2] px-4 py-2 text-xs text-slate-700">
                    {a}
                  </span>
                ))}
              </div>
              {product.applicationsExtra && (
                <p className="mt-4 max-w-xl text-xs leading-relaxed text-slate-500">{product.applicationsExtra}</p>
              )}
            </Reveal>
            <Reveal delay={0.24} className="mt-10 flex items-start gap-3 border border-black/10 bg-[#F5F5F2] p-6">
              <Package className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Packaging & delivery</p>
                <p className="mt-2 text-sm text-slate-700" data-testid="product-packaging">{product.packaging}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-amber-500/40 bg-white" data-testid="product-specs-card">
              <div className="flex items-center gap-3 border-b border-black/10 px-6 py-5">
                <FlaskConical className="h-5 w-5 text-amber-500" />
                <h2 className="font-display text-xl font-semibold uppercase text-[#0E1526]">Typical specifications</h2>
              </div>
              {product.gradeTable ? (
                <table className="w-full" data-testid="product-specs-table">
                  <thead>
                    <tr className="border-b border-black/10 text-left">
                      {product.gradeTable.columns.map((col, ci) => (
                        <th
                          key={col}
                          className={`py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 ${
                            ci === 0 ? "px-6" : ci === product.gradeTable.columns.length - 1 ? "px-6 text-right" : "px-4"
                          }`}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {product.gradeTable.rows.map((row) => (
                      <tr key={row[0]} className="border-b border-black/5 last:border-0 transition-colors hover:bg-[#F8FAFC]">
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`py-3.5 ${
                              ci === 0
                                ? "px-6 text-xs font-semibold text-[#0E1526]"
                                : ci === row.length - 1
                                  ? "px-6 text-right font-mono text-xs text-amber-600"
                                  : "px-4 font-mono text-[11px] text-slate-600"
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full" data-testid="product-specs-table">
                  <thead>
                    <tr className="border-b border-black/10 text-left">
                      <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Parameter</th>
                      <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Method</th>
                      <th className="px-6 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.specs.map((s) => (
                      <tr key={s.param} className="border-b border-black/5 last:border-0 transition-colors hover:bg-[#F8FAFC]">
                        <td className="px-6 py-3.5 text-xs text-slate-700">{s.param}</td>
                        <td className="px-4 py-3.5 font-mono text-[10px] uppercase text-slate-500">{s.standard}</td>
                        <td className="px-6 py-3.5 text-right font-mono text-xs text-amber-600">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="px-6 py-4 font-mono text-[10px] leading-relaxed text-slate-600">
                {product.specsNote ||
                  "Typical product specifications. Final specifications and batch-specific results are confirmed against the applicable Certificate of Analysis (CoA)."}
              </p>
            </div>
            {product.gradeNotes && (
              <div className="mt-8 border border-black/10 bg-[#F5F5F2] p-6" data-testid="grade-selection">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{product.notesTitle || "Grade selection"}</p>
                <div className="mt-4 space-y-4">
                  {product.gradeNotes.map((g) => (
                    <div key={g.grade}>
                      <p className="font-mono text-xs font-semibold text-amber-600">{g.grade}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">{g.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <Marquee items={[product.name, "CoA with every batch", "FOB — CFR — CIF", "Reply within one business day"]} />

      <section data-testid="product-inquiry-cta" className="bg-[#F5F5F2] py-24 noise-surface">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <Reveal><Eyebrow className="text-center">Ready to order?</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
              Request {product.shortName} pricing
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to={`/contact?product=${encodeURIComponent(product.name)}`}
              data-testid="product-quote-button"
              className="group mt-10 inline-flex items-center gap-2 bg-amber-500 px-10 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
            >
              Request a Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.28} className="mt-16 flex items-center justify-between border-t border-black/10 pt-8">
            <Link
              to={`/products/${prev.slug}`}
              data-testid="product-prev-link"
              className="group flex items-center gap-2 text-left text-sm text-slate-600 transition-colors hover:text-[#0E1526]"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              <span><span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Previous</span>{prev.shortName}</span>
            </Link>
            <Link
              to={`/products/${next.slug}`}
              data-testid="product-next-link"
              className="group flex items-center gap-2 text-right text-sm text-slate-600 transition-colors hover:text-[#0E1526]"
            >
              <span><span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Next</span>{next.shortName}</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
